import * as React from 'react';
import { X } from 'lucide-react';
import type { TFunction } from 'i18next';
import { LifecycleStatusEnum, type LifecycleStatus } from '@ooh/contracts';
import type { MediaUnitFilters } from '@ooh/contracts';
import { isBulkSelectionCapExceeded, shouldShowHiddenCount } from '../lib/bulk-selection-cap';
import { Button } from '@ooh/ui/components/button';
import { Label } from '@ooh/ui/components/label';
import { Input } from '@ooh/ui/components/input';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption as SelectOption,
} from '@ooh/ui/components/searchable-select-field';

// ─── Types ────────────────────────────────────────────────────

type BulkEditField = 'mediaType' | 'city' | 'lifecycleStatus' | 'supplier';

export interface BulkEditFieldValue {
  field: BulkEditField;
  value: string | number;
}

export interface BulkEditDrawerProps {
  isOpen: boolean;
  selectedCount: number;
  /** When non-null, the user activated "select all matching" — use filter mode */
  selectAllFilter: MediaUnitFilters | null;
  /** Total rows matching the current filter */
  totalMatching: number;
  /**
   * Of `selectedCount`, how many are not in the current results (id-based
   * selection only — a filter-based selection has no concept of "hidden").
   */
  hiddenCount?: number;
  onSubmit: (payload: BulkEditFieldValue) => Promise<void>;
  onCancel: () => void;
  t: TFunction<'inventory'>;
  isPending: boolean;
  serverError: string | null;
  /** MediaTypeEnum options (value = enum string, label = translated display name). */
  mediaTypeOptions: SelectOption[];
  /** Supplier options (value = numeric id as string, label = supplier name). */
  supplierOptions: SelectOption[];
  /** Distinct city names already in the catalog — offered as free-text autocomplete suggestions. */
  cities: string[];
}

type Step = 'field' | 'value' | 'confirm';

const BULK_EDIT_FIELDS: BulkEditField[] = ['mediaType', 'city', 'lifecycleStatus', 'supplier'];
const CITY_SUGGESTIONS_LIST_ID = 'bulk-edit-city-suggestions';

/** Resolves the human-readable label for a select-backed field's raw value. */
function resolveValueLabel(
  t: TFunction<'inventory'>,
  selectedField: BulkEditField,
  fieldValue: string,
  mediaTypeOptions: SelectOption[],
  supplierOptions: SelectOption[],
): string {
  if (selectedField === 'lifecycleStatus') {
    return t(`bulkEdit.lifecycleStatus.${fieldValue as LifecycleStatus}` as never);
  }
  if (selectedField === 'mediaType') {
    return mediaTypeOptions.find((o) => o.value === fieldValue)?.label ?? fieldValue;
  }
  if (selectedField === 'supplier') {
    return supplierOptions.find((o) => o.value === fieldValue)?.label ?? fieldValue;
  }
  return fieldValue;
}

// ─── Sub-components ───────────────────────────────────────────

interface FieldStepProps {
  t: TFunction<'inventory'>;
  onSelect: (field: BulkEditField) => void;
}

function FieldStep({ t, onSelect }: FieldStepProps) {
  return (
    <div data-testid="bulk-edit-field-step">
      <Label className="mb-2 block text-sm font-medium">{t('bulkEdit.fieldLabel')}</Label>
      <div className="grid gap-2" data-testid="bulk-edit-field-select">
        {BULK_EDIT_FIELDS.map((field) => (
          <Button
            key={field}
            type="button"
            variant="outline"
            data-testid={`bulk-edit-field-option-${field}`}
            className="justify-start"
            onClick={() => onSelect(field)}
          >
            {t(`bulkEdit.fields.${field}` as never)}
          </Button>
        ))}
      </div>
    </div>
  );
}

interface ValueStepProps {
  t: TFunction<'inventory'>;
  selectedField: BulkEditField;
  fieldValue: string;
  onChange: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  mediaTypeOptions: SelectOption[];
  supplierOptions: SelectOption[];
  cities: string[];
}

function ValueStep({
  t,
  selectedField,
  fieldValue,
  onChange,
  onNext,
  onBack,
  mediaTypeOptions,
  supplierOptions,
  cities,
}: ValueStepProps) {
  return (
    <div data-testid="bulk-edit-value-step">
      <p className="mb-4 text-sm text-muted-foreground">
        {t('bulkEdit.fieldLabel')}:{' '}
        <span className="font-medium text-foreground">
          {t(`bulkEdit.fields.${selectedField}` as never)}
        </span>
      </p>

      <Label className="mb-2 block text-sm font-medium">{t('bulkEdit.valueLabel')}</Label>

      {selectedField === 'lifecycleStatus' ? (
        <div className="grid gap-2" data-testid="bulk-edit-lifecycle-value">
          {LifecycleStatusEnum.options.map((status) => (
            <Button
              key={status}
              type="button"
              variant={fieldValue === status ? 'default' : 'outline'}
              data-testid={`bulk-edit-lifecycle-option-${status}`}
              className="justify-start"
              onClick={() => onChange(status)}
            >
              {t(`bulkEdit.lifecycleStatus.${status}` as never)}
            </Button>
          ))}
        </div>
      ) : selectedField === 'mediaType' ? (
        <SearchableSelectField
          options={mediaTypeOptions}
          value={fieldValue}
          onChange={onChange}
          placeholder={t('bulkEdit.selectPlaceholder')}
          data-testid="bulk-edit-mediatype-value"
        />
      ) : selectedField === 'supplier' ? (
        <SearchableSelectField
          options={supplierOptions}
          value={fieldValue}
          onChange={onChange}
          placeholder={t('bulkEdit.selectPlaceholder')}
          data-testid="bulk-edit-supplier-value"
        />
      ) : (
        <>
          <Input
            list={CITY_SUGGESTIONS_LIST_ID}
            data-testid="bulk-edit-text-value"
            value={fieldValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('form.placeholders.city')}
          />
          <datalist id={CITY_SUGGESTIONS_LIST_ID}>
            {cities.map((city) => (
              <option key={city} value={city} />
            ))}
          </datalist>
        </>
      )}

      <div className="mt-6 flex gap-3">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('bulkEdit.cancel')}
        </Button>
        <Button
          type="button"
          data-testid="bulk-edit-next-btn"
          disabled={!fieldValue}
          onClick={onNext}
        >
          {t('bulkEdit.nextBtn')}
        </Button>
      </div>
    </div>
  );
}

interface ConfirmStepProps {
  t: TFunction<'inventory'>;
  selectedField: BulkEditField;
  fieldValue: string;
  effectiveCount: number;
  hiddenCount: number;
  isFilterSelection: boolean;
  capExceeded: boolean;
  serverError: string | null;
  isPending: boolean;
  onSubmit: () => void;
  onBack: () => void;
  mediaTypeOptions: SelectOption[];
  supplierOptions: SelectOption[];
}

function ConfirmStep({
  t,
  selectedField,
  fieldValue,
  effectiveCount,
  hiddenCount,
  isFilterSelection,
  capExceeded,
  serverError,
  isPending,
  onSubmit,
  onBack,
  mediaTypeOptions,
  supplierOptions,
}: ConfirmStepProps) {
  return (
    <div data-testid="bulk-edit-confirm-step">
      <h3 className="mb-4 text-sm font-semibold">{t('bulkEdit.confirmTitle')}</h3>

      <div className="mb-4 rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm">
        <div className="mb-2">
          <span className="text-muted-foreground">{t('bulkEdit.fieldLabel')}: </span>
          <span className="font-medium">{t(`bulkEdit.fields.${selectedField}` as never)}</span>
        </div>
        <div className="mb-2">
          <span className="text-muted-foreground">{t('bulkEdit.valueLabel')}: </span>
          <span className="font-medium">
            {resolveValueLabel(t, selectedField, fieldValue, mediaTypeOptions, supplierOptions)}
          </span>
        </div>
        <div data-testid="bulk-edit-confirm-count">
          <span className="text-muted-foreground">
            {t('bulkEdit.confirmMessage', { count: effectiveCount })}
          </span>
          {shouldShowHiddenCount(isFilterSelection, hiddenCount) ? (
            <span className="block text-muted-foreground" data-testid="bulk-edit-hidden-count">
              {t('bulkEdit.hiddenCount', { count: hiddenCount })}
            </span>
          ) : null}
        </div>
      </div>

      {capExceeded ? (
        <div
          className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          data-testid="bulk-edit-cap-warning"
        >
          {t('bulkEdit.capWarning')}
        </div>
      ) : null}

      {serverError ? (
        <div
          className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          data-testid="bulk-edit-server-error"
        >
          {serverError}
        </div>
      ) : null}

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
          {t('bulkEdit.backBtn')}
        </Button>
        <Button
          type="button"
          data-testid="bulk-edit-submit-btn"
          disabled={isPending || capExceeded}
          onClick={onSubmit}
        >
          {isPending ? '...' : t('bulkEdit.submit')}
        </Button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────

export function BulkEditDrawer({
  isOpen,
  selectedCount,
  selectAllFilter,
  totalMatching,
  hiddenCount = 0,
  onSubmit,
  onCancel,
  t,
  isPending,
  serverError,
  mediaTypeOptions,
  supplierOptions,
  cities,
}: BulkEditDrawerProps) {
  const [step, setStep] = React.useState<Step>('field');
  const [selectedField, setSelectedField] = React.useState<BulkEditField | null>(null);
  const [fieldValue, setFieldValue] = React.useState<string>('');

  React.useEffect(() => {
    if (isOpen) {
      setStep('field');
      setSelectedField(null);
      setFieldValue('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isFilterSelection = selectAllFilter != null;
  const effectiveCount = isFilterSelection ? totalMatching : selectedCount;
  const capExceeded = isBulkSelectionCapExceeded(isFilterSelection, totalMatching, selectedCount);

  function handleFieldSelect(field: BulkEditField) {
    setSelectedField(field);
    setFieldValue('');
    setStep('value');
  }

  function handleNext() {
    if (!selectedField || !fieldValue) return;
    setStep('confirm');
  }

  function handleSubmitClick() {
    if (!selectedField || !fieldValue) return;
    // Only `supplier` submits a numeric id — `mediaType` is a MediaTypeEnum string
    // and must NOT be coerced. Coercing it used to run Number('billboard') -> NaN,
    // which serialized as `null` and made every mediaType bulk edit 400.
    const numericFields: BulkEditField[] = ['supplier'];
    const value = numericFields.includes(selectedField) ? Number(fieldValue) : fieldValue;
    void onSubmit({ field: selectedField, value });
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="bulk-edit-drawer">
      <div aria-hidden="true" className="absolute inset-0 bg-black/40" onClick={onCancel} />

      <div
        role="dialog"
        aria-labelledby="bulk-edit-drawer-title"
        className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-border bg-background shadow-xl"
        data-testid="bulk-edit-panel"
      >
        <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
          <h2 className="text-base font-semibold" id="bulk-edit-drawer-title">
            {t('bulkEdit.drawerTitle')}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('bulkEdit.cancel')}
            data-testid="bulk-edit-cancel-btn"
            onClick={onCancel}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          {step === 'field' ? <FieldStep t={t} onSelect={handleFieldSelect} /> : null}

          {step === 'value' && selectedField ? (
            <ValueStep
              t={t}
              selectedField={selectedField}
              fieldValue={fieldValue}
              onChange={setFieldValue}
              onNext={handleNext}
              onBack={() => setStep('field')}
              mediaTypeOptions={mediaTypeOptions}
              supplierOptions={supplierOptions}
              cities={cities}
            />
          ) : null}

          {step === 'confirm' && selectedField ? (
            <ConfirmStep
              t={t}
              selectedField={selectedField}
              fieldValue={fieldValue}
              effectiveCount={effectiveCount}
              hiddenCount={hiddenCount}
              isFilterSelection={isFilterSelection}
              capExceeded={capExceeded}
              serverError={serverError}
              isPending={isPending}
              onSubmit={handleSubmitClick}
              onBack={() => setStep('value')}
              mediaTypeOptions={mediaTypeOptions}
              supplierOptions={supplierOptions}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
